import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import type { GridPaginationModel } from "@mui/x-data-grid-premium";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import { AUTO_TAGGING_PRIVILEGES } from "../../../services/permissionsService";
import { AI_PLAN_ADD_TRIAGE_RULE_PATH, buildEditTriageRulePath } from "../constants";
import TriagePlanFilterBar, {
  type TriagePlanProcessFlowOption,
} from "./TriagePlanFilterBar";
import TriagePlanTable from "./TriagePlanTable";
import { fetchTriageProcessFlows } from "./triageProcessFlows";
import type { TriagePlanRow } from "./types";

const DEFAULT_PAGE_SIZE = 10;

const EMPTY_TRIAGE_CONDITION = {
  combinator: "and",
  not: false,
  rules: [],
} as const;

const TRIAGE1_CONDITION = {
  combinator: "and",
  not: false,
  rules: [
    {
      field: "ProductType",
      operator: "containsAny",
      value: [{ code: "large_molecule", name: "Large Molecule" }],
      valueSource: "value",
    },
    {
      field: "Topic",
      operator: "containsAny",
      value: [{ code: "safety_concern", name: "Safety Concern" }],
      valueSource: "value",
    },
    {
      field: "HealthAuthority",
      operator: "containsAny",
      value: [{ code: "fda", name: "FDA" }],
      valueSource: "value",
    },
  ],
} as const;

/**
 * Preview rows for Triage Plan list layout until the list API is wired.
 * Matches the Figma list columns: Condition / Process Flow / Active / Actions.
 */
const TRIAGE_PLAN_PREVIEW_ROWS: TriagePlanRow[] = [
  {
    id: "TRIAGE1",
    objectType: "insight",
    objectSubType: "regulation",
    objectTypeName: "Insight",
    objectSubTypeName: "Regulation",
    conditionDescription:
      "Product Type = Large Molecule and Topic= Safety Concern and Health Authority = FDA",
    processFlow: "1",
    processFlowName: "Auto Alert Flow",
    definitionId: "def-1",
    enabled: true,
    condition: TRIAGE1_CONDITION,
  },
  {
    id: "TRIAGE2",
    objectType: "insight",
    objectSubType: "regulation",
    objectTypeName: "Insight",
    objectSubTypeName: "Regulation",
    conditionDescription: "If country = China and Product Type = Biosimilar",
    processFlow: "1",
    processFlowName: "Process Flow Name",
    definitionId: "def-1",
    enabled: true,
    condition: EMPTY_TRIAGE_CONDITION,
  },
  {
    id: "TRIAGE3",
    objectType: "insight",
    objectSubType: "",
    objectTypeName: "Insight",
    objectSubTypeName: "",
    conditionDescription: "All",
    processFlow: "1",
    processFlowName: "Process Flow Name",
    definitionId: "def-1",
    enabled: true,
    condition: EMPTY_TRIAGE_CONDITION,
  },
  {
    id: "TRIAGE4",
    objectType: "insight",
    objectSubType: "",
    objectTypeName: "Insight",
    objectSubTypeName: "",
    conditionDescription: "All",
    processFlow: "1",
    processFlowName: "Process Flow Name",
    definitionId: "def-1",
    enabled: true,
    condition: EMPTY_TRIAGE_CONDITION,
  },
  {
    id: "TRIAGE5",
    objectType: "insight",
    objectSubType: "",
    objectTypeName: "Insight",
    objectSubTypeName: "",
    conditionDescription: "All",
    processFlow: "1",
    processFlowName: "Process Flow Name",
    definitionId: "def-1",
    enabled: true,
    condition: EMPTY_TRIAGE_CONDITION,
  },
  {
    id: "TRIAGE6",
    objectType: "insight",
    objectSubType: "",
    objectTypeName: "Insight",
    objectSubTypeName: "",
    conditionDescription: "All",
    processFlow: "1",
    processFlowName: "Process Flow Name",
    definitionId: "def-1",
    enabled: true,
    condition: EMPTY_TRIAGE_CONDITION,
  },
  {
    id: "TRIAGE7",
    objectType: "insight",
    objectSubType: "",
    objectTypeName: "Insight",
    objectSubTypeName: "",
    conditionDescription: "All",
    processFlow: "1",
    processFlowName: "Process Flow Name",
    definitionId: "def-1",
    enabled: true,
    condition: EMPTY_TRIAGE_CONDITION,
  },
  {
    id: "TRIAGE8",
    objectType: "insight",
    objectSubType: "",
    objectTypeName: "Insight",
    objectSubTypeName: "",
    conditionDescription: "All",
    processFlow: "1",
    processFlowName: "Process Flow Name",
    definitionId: "def-1",
    enabled: true,
    condition: EMPTY_TRIAGE_CONDITION,
  },
  {
    id: "TRIAGE9",
    objectType: "insight",
    objectSubType: "",
    objectTypeName: "Insight",
    objectSubTypeName: "",
    conditionDescription: "All",
    processFlow: "1",
    processFlowName: "Process Flow Name",
    definitionId: "def-1",
    enabled: true,
    condition: EMPTY_TRIAGE_CONDITION,
  },
  {
    id: "TRIAGE10",
    objectType: "insight",
    objectSubType: "",
    objectTypeName: "Insight",
    objectSubTypeName: "",
    conditionDescription: "All",
    processFlow: "1",
    processFlowName: "Process Flow Name",
    definitionId: "def-1",
    enabled: true,
    condition: EMPTY_TRIAGE_CONDITION,
  },
];

export default function TriagePlanTab() {
  const navigate = useNavigate();
  const canManageAutoTagging = useHasPrivilege(AUTO_TAGGING_PRIVILEGES.manage);
  const [processFlow, setProcessFlow] = useState("");
  const [processFlowOptions, setProcessFlowOptions] = useState<
    readonly TriagePlanProcessFlowOption[]
  >([]);
  const [processFlowOptionsLoading, setProcessFlowOptionsLoading] =
    useState(true);
  const [rows, setRows] = useState<TriagePlanRow[]>(TRIAGE_PLAN_PREVIEW_ROWS);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  useEffect(() => {
    let cancelled = false;
    setProcessFlowOptionsLoading(true);
    fetchTriageProcessFlows()
      .then((flows) => {
        if (cancelled) return;
        setProcessFlowOptions(
          flows.map((item) => ({
            code: item.id,
            name: item.name,
          })),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setProcessFlowOptions([]);
      })
      .finally(() => {
        if (!cancelled) setProcessFlowOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 },
    );
  }, [processFlow]);

  const filteredRows = useMemo(() => {
    if (!processFlow.trim()) return rows;
    return rows.filter((row) => row.processFlow === processFlow);
  }, [processFlow, rows]);

  const pagedRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return filteredRows.slice(start, start + paginationModel.pageSize);
  }, [filteredRows, paginationModel.page, paginationModel.pageSize]);

  const handleAddNew = useCallback(() => {
    if (!canManageAutoTagging) return;
    navigate(AI_PLAN_ADD_TRIAGE_RULE_PATH);
  }, [canManageAutoTagging, navigate]);

  const handleEdit = useCallback(
    (row: TriagePlanRow) => {
      if (!canManageAutoTagging) return;
      navigate(buildEditTriageRulePath(row.id), { state: { rule: row } });
    },
    [canManageAutoTagging, navigate],
  );

  const handleActiveChange = useCallback((rowId: string, enabled: boolean) => {
    setRows((prev) =>
      prev.map((item) => (item.id === rowId ? { ...item, enabled } : item)),
    );
  }, []);

  const handleDeleteRow = useCallback((rowId: string) => {
    setRows((prev) => prev.filter((item) => item.id !== rowId));
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        flex: 1,
        minHeight: 0,
        mx: "16px",
      }}
    >
      <TriagePlanFilterBar
        processFlow={processFlow}
        processFlowOptions={processFlowOptions}
        loading={processFlowOptionsLoading}
        onProcessFlowChange={setProcessFlow}
      />
      <TriagePlanTable
        rows={pagedRows}
        rowCount={filteredRows.length}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        onAddNew={canManageAutoTagging ? handleAddNew : undefined}
        onEdit={canManageAutoTagging ? handleEdit : undefined}
        onActiveChange={handleActiveChange}
        onDeleteRow={handleDeleteRow}
      />
    </Box>
  );
}
